import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const emptyTest = {
  title: "",
  slug: "",
  category: "mental-health",
  description: "",
  long_description: "",
  image: "",
  price: 0,
  duration_minutes: 10,
  questions: [],
  tiers: []
};

export default function AdminTests() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTest);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/admin/tests`).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(emptyTest); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...emptyTest, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/tests/${editing.id}`, form);
      else await api.post(`/admin/tests`, form);
      toast.success("Tersimpan");
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Yakin hapus?")) return;
    try { await api.delete(`/admin/tests/${id}`); load(); toast.success("Dihapus"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image: data.url });
      toast.success("Foto berhasil diunggah");
    } catch (err) {
      toast.error("Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  // Questions Helpers
  const addQuestion = () => setForm({ ...form, questions: [...form.questions, { text: "", options: [] }] });
  const updateQuestion = (i, val) => {
    const n = [...form.questions]; n[i].text = val; setForm({ ...form, questions: n });
  };
  const removeQuestion = (i) => {
    const n = [...form.questions]; n.splice(i, 1); setForm({ ...form, questions: n });
  };
  
  const addOption = (qi) => {
    const n = [...form.questions]; n[qi].options.push({ label: "", value: 0 }); setForm({ ...form, questions: n });
  };
  const updateOption = (qi, oi, key, val) => {
    const n = [...form.questions]; n[qi].options[oi][key] = val; setForm({ ...form, questions: n });
  };
  const removeOption = (qi, oi) => {
    const n = [...form.questions]; n[qi].options.splice(oi, 1); setForm({ ...form, questions: n });
  };

  // Tiers Helpers
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { min_score: 0, max_score: 10, title: "", description: "" }] });
  const updateTier = (i, key, val) => {
    const n = [...form.tiers]; n[i][key] = val; setForm({ ...form, tiers: n });
  };
  const removeTier = (i) => {
    const n = [...form.tiers]; n.splice(i, 1); setForm({ ...form, tiers: n });
  };

  return (
    <div data-testid={`admin-tests-page`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[#1A1728]">Tes Psikologi</h1>
          <p className="text-[#5C5870] mt-1">{items.length} item</p>
        </div>
        <button onClick={startNew} className="lumina-btn-primary" data-testid={`add-test-btn`}>
          <Plus className="w-4 h-4" /> Tambah Tes
        </button>
      </div>

      <div className="lumina-card overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[#E2DFED] bg-[#F8F7FA]">
              <th className="py-4 px-6 font-semibold text-[#1A1728]">Judul</th>
              <th className="py-4 px-6 font-semibold text-[#1A1728]">Kategori</th>
              <th className="py-4 px-6 font-semibold text-[#1A1728]">Pertanyaan</th>
              <th className="py-4 px-6 font-semibold text-[#1A1728]">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[#E2DFED] last:border-0 hover:bg-[#F8F7FA]/50 transition-colors">
                <td className="py-4 px-6 text-[#5C5870] font-medium">{it.title}</td>
                <td className="py-4 px-6 text-[#5C5870]">{it.category}</td>
                <td className="py-4 px-6 text-[#5C5870]">{it.questions?.length || 0}</td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(it)} className="p-2 text-[#6B4BA3] hover:bg-[#F1EBFA] rounded-xl"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(it.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-xl"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center text-[#5C5870]">Belum ada data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Tes" : "Tambah Tes"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-8 mt-4">
            
            {/* Info Dasar */}
            <div className="p-5 border border-[#E2DFED] rounded-xl space-y-4 bg-white">
              <h3 className="font-serif text-xl font-bold text-[#1A1728]">Informasi Dasar</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="lumina-label block mb-2">Judul</label>
                  <input value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" required />
                </div>
                <div>
                  <label className="lumina-label block mb-2">Slug</label>
                  <input value={form.slug} onChange={e=>setForm({...form, slug: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" required />
                </div>
                <div>
                  <label className="lumina-label block mb-2">Kategori</label>
                  <input value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" required />
                </div>
                <div>
                  <label className="lumina-label block mb-2">Harga (0 = Gratis)</label>
                  <input type="number" value={form.price} onChange={e=>setForm({...form, price: Number(e.target.value)})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" />
                </div>
                <div>
                  <label className="lumina-label block mb-2">Durasi (Menit)</label>
                  <input type="number" value={form.duration_minutes} onChange={e=>setForm({...form, duration_minutes: Number(e.target.value)})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" />
                </div>
                <div className="col-span-2">
                  <label className="lumina-label block mb-2">Gambar Header (Opsional)</label>
                  <input type="file" accept="image/*" disabled={uploading} onChange={handleUpload} className="w-full text-sm file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F1EBFA] file:text-[#6B4BA3] cursor-pointer border border-[#E2DFED] rounded-xl p-2" />
                  {form.image && <img src={form.image?.startsWith("/") ? (process.env.REACT_APP_BACKEND_URL || "") + form.image : form.image} alt="Preview" className="h-20 object-cover rounded-xl mt-2" />}
                </div>
                <div className="col-span-2">
                  <label className="lumina-label block mb-2">Deskripsi Singkat</label>
                  <textarea value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED]" rows={2} required />
                </div>
              </div>
            </div>

            {/* Pertanyaan */}
            <div className="p-5 border border-[#E2DFED] rounded-xl space-y-4 bg-[#F8F7FA]">
              <div className="flex justify-between items-center">
                <h3 className="font-serif text-xl font-bold text-[#1A1728]">Daftar Pertanyaan</h3>
                <button type="button" onClick={addQuestion} className="px-3 py-1.5 bg-[#6B4BA3] text-white text-sm rounded-lg flex items-center gap-1"><Plus className="w-4 h-4"/> Pertanyaan</button>
              </div>
              
              <div className="space-y-4">
                {form.questions.map((q, qi) => (
                  <div key={qi} className="p-4 bg-white border border-[#E2DFED] rounded-xl relative">
                    <button type="button" onClick={() => removeQuestion(qi)} className="absolute top-4 right-4 text-red-500"><X className="w-5 h-5"/></button>
                    <label className="font-semibold text-sm mb-2 block">Pertanyaan {qi + 1}</label>
                    <input value={q.text} onChange={e => updateQuestion(qi, e.target.value)} placeholder="Teks pertanyaan..." className="w-full px-4 py-2 rounded-lg border border-[#E2DFED] mb-3" required />
                    
                    <div className="pl-4 border-l-2 border-[#E8F0FA] space-y-2">
                      <p className="text-xs font-semibold text-[#5C5870]">PILIHAN JAWABAN:</p>
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex gap-2 items-center">
                          <input value={opt.label} onChange={e => updateOption(qi, oi, 'label', e.target.value)} placeholder="Teks Pilihan" className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" required />
                          <input type="number" value={opt.value} onChange={e => updateOption(qi, oi, 'value', Number(e.target.value))} placeholder="Poin" className="w-20 px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" required />
                          <button type="button" onClick={() => removeOption(qi, oi)} className="text-red-400 p-1"><X className="w-4 h-4"/></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => addOption(qi)} className="text-xs text-[#6B4BA3] font-medium">+ Tambah Pilihan</button>
                    </div>
                  </div>
                ))}
                {form.questions.length === 0 && <p className="text-sm text-gray-500">Belum ada pertanyaan.</p>}
              </div>
            </div>

            {/* Tiers / Hasil */}
            <div className="p-5 border border-[#E2DFED] rounded-xl space-y-4 bg-white">
              <div className="flex justify-between items-center">
                <h3 className="font-serif text-xl font-bold text-[#1A1728]">Tingkatan Hasil (Tiers)</h3>
                <button type="button" onClick={addTier} className="px-3 py-1.5 bg-[#6B4BA3] text-white text-sm rounded-lg flex items-center gap-1"><Plus className="w-4 h-4"/> Tier</button>
              </div>
              <p className="text-sm text-gray-500 -mt-2 mb-4">Tentukan teks hasil tes berdasarkan total skor poin pilihan jawaban.</p>
              
              <div className="space-y-4">
                {form.tiers.map((t, ti) => (
                  <div key={ti} className="p-4 border border-[#E2DFED] rounded-xl relative grid grid-cols-2 gap-4">
                    <button type="button" onClick={() => removeTier(ti)} className="absolute top-4 right-4 text-red-500"><X className="w-5 h-5"/></button>
                    <div className="col-span-2 md:col-span-1 flex gap-2">
                      <div>
                        <label className="text-xs font-semibold block mb-1">Skor Min</label>
                        <input type="number" value={t.min_score} onChange={e => updateTier(ti, 'min_score', Number(e.target.value))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" required />
                      </div>
                      <div>
                        <label className="text-xs font-semibold block mb-1">Skor Max</label>
                        <input type="number" value={t.max_score} onChange={e => updateTier(ti, 'max_score', Number(e.target.value))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" required />
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="text-xs font-semibold block mb-1">Judul Hasil</label>
                      <input value={t.title} onChange={e => updateTier(ti, 'title', e.target.value)} className="w-full px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" required />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-semibold block mb-1">Deskripsi Hasil</label>
                      <textarea value={t.description} onChange={e => updateTier(ti, 'description', e.target.value)} className="w-full px-3 py-1.5 text-sm rounded-lg border border-[#E2DFED]" rows={2} required />
                    </div>
                  </div>
                ))}
                {form.tiers.length === 0 && <p className="text-sm text-gray-500">Belum ada tingkatan hasil.</p>}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#E2DFED]">
              <button type="button" onClick={() => setOpen(false)} className="px-6 py-2.5 rounded-full border border-[#E2DFED] text-[#5C5870] font-medium hover:bg-gray-50">Batal</button>
              <button type="submit" className="px-6 py-2.5 rounded-full bg-[#1A1728] text-white font-medium hover:bg-black flex items-center gap-2"><Save className="w-4 h-4"/> Simpan Tes</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
